import { useState, useEffect } from 'react';
import { Formik, Form } from 'formik';
import { isEqual } from 'lodash';
import Yup from '../../utils/yup';

import addNotification from '../../utils/addNotification';
import { NotificationType } from '../../config';
import useMenus from '../../context/MenuContext';
import { FormButtonPanelUI, FormHeaderUI, FormBase } from '../Interface';
import { cleanValues } from '../../utils/helper';
import dayjs from 'dayjs';
import BatchSnFields from './BatchSnFields';
import BatchSnTabs from './BatchSnTabs';
import BatchSnReports from './BatchSnReports';
import batchSnService from '../../service/batchSn.service';

export const BatchSnPage = () => {
  const { menus, getMenus, getMenuSubMenuId, getCanEdit } = useMenus();
  const [refetchTable, setRefetchTable] = useState(false);
  const [viewField, setViewField] = useState(true);
  const [editButtonVisible, setEditButtonvisible] = useState(true);
  const [canEdit, setCanEdit] = useState(true);

  const [defaultValues, setDefaultValues] = useState({});
  const [selectedValues, setSelectedValues] = useState({});
  const [defaultSelectedValues, setDefaultSelectedValues] = useState({});
  const [operatorsDisabled, setOperatorsDisabled] = useState(false);
  const [initValues, setInitValues] = useState({});
  const [rowSelectionModel, setRowSelectionModel] = useState([]);

  const def = {
    itemCode: '',
    lot: '',
    batchSn: '',
    addDate: null,
    supplierId: '',
    expDate: null,
    mnfDate: null,
    batchAttribute1: '',
    batchAttribute2: '',
    comment: '',
    price: '',
    other: '',
    batchSnAttachment: [],
  };

  useEffect(() => {
    getMenus();

    setDefaultValues(def);
    setDefaultSelectedValues(def);
  }, []);

  useEffect(() => {
    const submenuId = getMenuSubMenuId('batchSn');
    setCanEdit(getCanEdit(submenuId));
  }, [menus]);

  useEffect(() => {
    if (Object.keys(selectedValues).length > 0) {
      if (!isEqual(defaultSelectedValues, selectedValues)) {
        valuesToForm();
        setOperatorsDisabled(false);
      } else {
        setOperatorsDisabled(true);
        setInitValues(defaultValues);
      }
    } else {
      setOperatorsDisabled(true);
      setInitValues(defaultValues);
    }
  }, [selectedValues]);

  const validationSchema = Yup.object().shape({
    supplierId: Yup.string().nullable(),
    expDate: Yup.string().nullable(),
    mnfDate: Yup.string().nullable(),
    batchAttribute1: Yup.string().nullable(),
    batchAttribute2: Yup.string().nullable(),
    comment: Yup.string().nullable(),
    price: Yup.number().nullable(),
    other: Yup.string().nullable(),
  });

  const valuesToForm = () => {
    const {
      batchSnId,
      itemCode,
      lot,
      batchSn,
      addDate,
      supplierId,
      expDate,
      mnfDate,
      batchAttribute1,
      batchAttribute2,
      comment,
      price,
      other,
      batchSnAttachment,
    } = selectedValues;

    setInitValues({
      batchSnId,
      itemCode,
      lot: lot || '',
      batchSn,
      addDate: dayjs(addDate),
      supplierId: supplierId || '',
      expDate: expDate ? dayjs(expDate) : null,
      mnfDate: mnfDate ? dayjs(mnfDate) : null,
      batchAttribute1: batchAttribute1 || '',
      batchAttribute2: batchAttribute2 || '',
      comment: comment || '',
      price: price || '',
      other: other || '',
      batchSnAttachment,
    });
  };

  const updateObject = async (values) => {
    const updData = {
      ...cleanValues(values),
      price: parseFloat(values.price),
      expDate: dayjs(values.expDate).isValid()
        ? dayjs(values.expDate).startOf('day').add(12, 'hour').toISOString()
        : null,
      mnfDate: dayjs(values.mnfDate).isValid()
        ? dayjs(values.mnfDate).startOf('day').add(12, 'hour').toISOString()
        : null,
    };

    updData.existingFiles = updData.batchSnAttachment?.map((f) => f.name);

    const formData = new FormData();
    formData.append('data', JSON.stringify(updData));

    if (updData.batchSnAttachment) {
      for (const file of updData.batchSnAttachment) {
        if (file instanceof File) {
          formData.append('files', file);
        }
      }
    }

    batchSnService.updateData(formData).then((data) => {
      addNotification({
        content: 'Sikeres módosítás!',
        type: NotificationType.SUCCESS,
      });

      setSelectedValues(data);
      setRefetchTable(true);
      setEditButtonvisible(true);
      setViewField(true);
    });
  };

  return (
    <FormBase
      name="BatchSn"
      leftPanelContent={
        <>
          <div className="h-full overflow-auto custom-scrollbar">
            <FormHeaderUI
              modClick={() => {
                setViewField(false);
                setEditButtonvisible(false);
              }}
              modDisabled={!viewField || operatorsDisabled || !canEdit}
            />

            <Formik
              initialValues={initValues}
              validationSchema={validationSchema}
              validateOnChange={false}
              onSubmit={updateObject}
              enableReinitialize
            >
              {({ resetForm }) => (
                <Form>
                  <BatchSnFields viewField={viewField} />

                  <BatchSnTabs disabled={viewField} />

                  <FormButtonPanelUI
                    onCancelClick={() => {
                      setViewField(true);
                      setEditButtonvisible(true);

                      resetForm();
                    }}
                    disabled={editButtonVisible}
                  />
                </Form>
              )}
            </Formik>
          </div>
        </>
      }
      rightPanelContent={
        <BatchSnReports
          refetch={{ refetchTable, setRefetchTable }}
          values={{ selectedValues, setSelectedValues }}
          viewField={viewField}
          operatorsDisabled={operatorsDisabled}
          rowSelection={{ rowSelectionModel, setRowSelectionModel }}
        />
      }
    />
  );
};
